import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import UserQuestionTable from '../components/userQuestion/UserQuestionTable';
import UserQuestionForm from '../components/userQuestion/UserQuestionForm';
import UserQuestionService from '../services/userQuestionService';
import { UserQuestion } from '../types/UserQuestion';
import { useAuth } from '../hooks/useAuth';

const UserQuestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [questions, setQuestions] = useState<UserQuestion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<UserQuestion | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    // Check if the user is an admin
    if (user && user.role === 'admin') {
      setIsAdmin(true);
    }
  }, [user]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      let data;
      
      // Admin sees all questions, regular users only see their own
      if (isAdmin) {
        data = await UserQuestionService.getAllUserQuestions();
      } else if (user) {
        data = await UserQuestionService.getUserQuestionsByEmail(user.email);
      } else {
        setQuestions([]);
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      setQuestions(data);
      setError(null);
    } catch (err) {
      setError('Error loading questions. Please try again later.');
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [isAdmin, user]);

  const handleCreateQuestion = () => {
    setEditingQuestion(null);
    setIsFormOpen(true);
  };

  const handleEditQuestion = (question: UserQuestion) => {
    setEditingQuestion(question);
    setIsFormOpen(true);
  };

  const handleDeleteQuestion = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await UserQuestionService.deleteUserQuestion(id);
      // Refresh the question list
      fetchQuestions();
    } catch (err: any) {
      setError('Error deleting question. Please try again.');
      console.error('Error deleting question:', err);
    }
  };

  const handleFormSubmit = async (question: UserQuestion) => {
    try {
      if (editingQuestion) {
        // Update existing question
        await UserQuestionService.updateUserQuestion(editingQuestion.id!, question);
      } else {
        // Create new question
        await UserQuestionService.createUserQuestion(question);
      }

      // Close the form and refresh questions
      setIsFormOpen(false);
      setEditingQuestion(null);
      fetchQuestions();
    } catch (err: any) {
      setError(err.message || 'Error saving question. Please try again.');
      console.error('Error saving question:', err);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingQuestion(null);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">User Questions</span>
          </div>

          <div className="content-container">
            <div className="page-header">
              <h1>User Questions</h1>
              <button 
                className="primary-button"
                onClick={handleCreateQuestion}
                disabled={isFormOpen}
              >
                Add New Question
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {isFormOpen ? (
              <UserQuestionForm 
                question={editingQuestion}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                disableEmailEdit={!isAdmin}
              />
            ) : (
              <UserQuestionTable
                questions={questions}
                loading={loading}
                onEdit={handleEditQuestion}
                onDelete={handleDeleteQuestion}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserQuestionsPage; 